import Page from "@/lib/pages/Page";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import {Color} from "@/lib/styles";
import Radio from "@/lib/components/Radio";
import {useEffect, useState} from "react";
import {ChatPreferences} from "@/lib/types";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useSession} from "@/lib/auth/auth";
import {useGlobalSearchParams, useRouter} from "expo-router";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import ErrorPage from "@/lib/pages/ErrorPage";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {ActivityIndicator} from "react-native";

export default function Preferences() {
    const [muted, setMuted] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [saveError, setSaveError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const {chat_id} = useGlobalSearchParams();
    const {session} = useSession();
    const queryClient = useQueryClient();
    const router = useRouter();

    const queryKey = ['chat', chat_id, 'preferences', session];
    const {data, isPending, error} = useQuery<ChatPreferences>({
        queryKey,
        queryFn: () => fetchBackend(`/chat/${chat_id}/preferences`, "GET", session),
        retry: dataRetryPolicy,
        enabled: !!session,
    });

    const savePreferences = async (changes: ChatPreferences) => {
        setSaveError(null);
        setBusy(true);
        fetchBackendNoContent(`/chat/${chat_id}/preferences`, "POST", session, changes).then(async () => {
            await queryClient.invalidateQueries({queryKey});
            setBusy(false);
            router.dismiss();
        }).catch((error) => {
            setSaveError(error);
            setBusy(false);
        });
    }

    useEffect(() => {
        if (!data) {
            return;
        } else if (!data.subscribed) {
            // user has no chat subscription, but might make one by saving preferences
            setMuted(true);
            setHidden(true);
        } else {
            setMuted(data.muted);
            setHidden(data.hidden);
        }
    }, [data])

    if (error) {
        return <ErrorPage error={error}/>
    } else if (isPending || !data) {
        return <SpinnerPage/>;
    }

    return (
        <Page>
            <Radio
                selected={!muted}
                checkbox={true}
                onPress={() => setMuted(!muted)}
                title={"Motta varslinger"}
                description={"Du varsles på mobil når det kommer nye meldinger."}
            />
            <Radio
                selected={!hidden}
                checkbox={true}
                onPress={() => setHidden(!hidden)}
                title={"Synlig i oversikten"}
                description={"Vis meg denne chatten i hovedmenyen."}
            />

            {busy && <ActivityIndicator color={Color.PrimaryText} size={"small"}/>}
            {saveError && <ErrorMessage error={saveError}/>}

            <Nav>
                <ActionButton
                    onPress={() => savePreferences({hidden, muted, subscribed: true})}
                    color={Color.Green}
                    title={"Lagre"}
                />
            </Nav>

        </Page>
    )
}